import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import useSWR from 'swr';
import { RequestIssueWithData } from '../../../../../../../typings/Issue.interface';
import { APISource } from '../../../../../../../typings/Source.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { RelativeDate } from '../../../../utils/date';
import Button from '../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../common/base/Card';
import Text from '../../../common/base/Text';
import { BulkOperationProgress } from '../../../common/BulkOperations';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { DashboardContext } from '../DashboardContext';
import Histogram from '../Events/Histogram';
import { RequestOverview } from '../Request';
const RequestIssue: React.FC<{
  issue: RequestIssueWithData;
  render_data: {
    sources_by_id: Record<string, APISource>;
  };
  setDate: (query_date: object) => void;
  start_date: Date;
  end_date: Date;
  relative_date?: RelativeDate;
  refresh_key: string;
}> = t0 => {
  const $ = _c(62);
  const {
    issue,
    start_date,
    end_date,
    setDate,
    relative_date,
    refresh_key,
    render_data
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription,
    organization
  } = useContext(DashboardContext);
  const request = issue.data?.request;
  const source_id = issue.aggregation_keys.source_id[0];
  const cached_source = render_data.sources_by_id?.[source_id];
  let t1;
  if ($[0] !== cached_source || $[1] !== source_id) {
    t1 = !cached_source && source_id && APIMethodKeys.sources.get(source_id);
    $[0] = cached_source;
    $[1] = source_id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== HookdeckAPI.sources || $[4] !== source_id) {
    t2 = () => HookdeckAPI.sources.get(source_id);
    $[3] = HookdeckAPI.sources;
    $[4] = source_id;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data: fetched_source,
    error: source_error,
    isLoading: source_loading
  } = useSWR(t1, t2);
  const source = cached_source || fetched_source;
  let bulk_retry_filters;
  let t3;
  let t4;
  if ($[6] !== issue.aggregation_keys.rejection_cause || $[7] !== issue.aggregation_keys.source_id) {
    bulk_retry_filters = {
      source_id: issue.aggregation_keys.source_id,
      rejection_cause: issue.aggregation_keys.rejection_cause,
      in_progress: true
    };
    t3 = useSWR;
    t4 = APIMethodKeys.bulk.requests.list(bulk_retry_filters);
    $[6] = issue.aggregation_keys.rejection_cause;
    $[7] = issue.aggregation_keys.source_id;
    $[8] = bulk_retry_filters;
    $[9] = t3;
    $[10] = t4;
  } else {
    bulk_retry_filters = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[11] !== HookdeckAPI.bulk.requests || $[12] !== bulk_retry_filters) {
    t5 = () => HookdeckAPI.bulk.requests.list(bulk_retry_filters);
    $[11] = HookdeckAPI.bulk.requests;
    $[12] = bulk_retry_filters;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      refreshInterval: 5000
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    data: current_bulk_retries
  } = t3(t4, t5, t6);
  const current_bulk_retry = current_bulk_retries?.models && current_bulk_retries.models.length > 0 && current_bulk_retries.models[0];
  const t7 = issue.aggregation_keys.source_id;
  const t8 = issue.aggregation_keys.rejection_cause;
  let t9;
  if ($[15] !== start_date) {
    t9 = start_date.toISOString();
    $[15] = start_date;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== end_date) {
    t10 = end_date.toISOString();
    $[17] = end_date;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== relative_date || $[20] !== t10 || $[21] !== t9) {
    t11 = {
      relative: relative_date,
      min: t9,
      max: t10
    };
    $[19] = relative_date;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== issue.aggregation_keys.rejection_cause || $[24] !== issue.aggregation_keys.source_id || $[25] !== t11) {
    t12 = {
      source_id: t7,
      rejection_cause: t8,
      date: t11
    };
    $[23] = issue.aggregation_keys.rejection_cause;
    $[24] = issue.aggregation_keys.source_id;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const filters = t12;
  let t13;
  if ($[27] !== current_bulk_retry) {
    t13 = current_bulk_retry && <StyledCard m={{
      b: 8
    }}><StyledCardSection p={{
        x: 4,
        y: 3
      }}><Text semi={true}>Bulk retry in progress</Text></StyledCardSection><StyledCardSection p={4}><BulkOperationProgress operation_type="retry" model="requests" operation={current_bulk_retry} /></StyledCardSection></StyledCard>;
    $[27] = current_bulk_retry;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Text bold={true} size="l" m={{
      b: 4
    }}>Rejected requests histogram</Text>;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      x: 4,
      y: 3
    };
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== end_date || $[32] !== filters || $[33] !== refresh_key || $[34] !== setDate || $[35] !== start_date) {
    t16 = <StyledCardSection p={t15}><Histogram model="Requests" dimention="rejection_cause" filters={filters} start_date={start_date} end_date={end_date} setDate={setDate} allow_reset={false} refresh_key={refresh_key} show_date_filter={true} /></StyledCardSection>;
    $[31] = end_date;
    $[32] = filters;
    $[33] = refresh_key;
    $[34] = setDate;
    $[35] = start_date;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  let t18;
  let t19;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      y: 3,
      x: 4
    };
    t18 = {
      justify: "space-between",
      align: "center"
    };
    t19 = <Text muted={true}>Requests rejected by this source matching this issue's criteria.</Text>;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
  } else {
    t17 = $[37];
    t18 = $[38];
    t19 = $[39];
  }
  let t20;
  if ($[40] !== issue.aggregation_keys.rejection_cause) {
    t20 = issue.aggregation_keys.rejection_cause.join(",");
    $[40] = issue.aggregation_keys.rejection_cause;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== start_date) {
    t21 = start_date.toISOString();
    $[42] = start_date;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== end_date) {
    t22 = end_date.toISOString();
    $[44] = end_date;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  const t23 = `/requests?source_id=${source_id}&rejection_cause=${t20}&date[min]=${t21}&date[max]=${t22}`;
  let t24;
  if ($[46] !== t23) {
    t24 = <StyledCardSection p={t17} flex={t18}>{t19}<Button icon="link" outline={true} to={t23}>View requests</Button></StyledCardSection>;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t16 || $[49] !== t24) {
    t25 = <StyledCard>{t16}{t24}</StyledCard>;
    $[48] = t16;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== organization || $[52] !== request || $[53] !== source || $[54] !== source_error || $[55] !== source_loading || $[56] !== subscription) {
    t26 = subscription && organization && request && <RequestOverview request={request} source={source} source_error={source_error} source_loading={source_loading} rejection_cause_breakdown={null} subscription={subscription} organization={organization} embedded={true} embedded_metadata_title="Request metadata" />;
    $[51] = organization;
    $[52] = request;
    $[53] = source;
    $[54] = source_error;
    $[55] = source_loading;
    $[56] = subscription;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t13 || $[59] !== t25 || $[60] !== t26) {
    t27 = <>{t13}{t14}{t25}{t26}</>;
    $[58] = t13;
    $[59] = t25;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  return t27;
};
export default RequestIssue;